import { useId, type ComponentProps, type CSSProperties, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { tagVar } from './tag-colors'
import { FlameIcon } from './vonk-icons'

export interface ChipProps extends Omit<ComponentProps<'span'>, 'color'> {
  /** Palette key (`tag-colors.ts`); unknown keys render grey. */
  color: string
  /** `tag` = category (Gloed: glass pill with a gradient disc), `pill` = label (Hitteschaal: gradient ring, VONK flame). */
  shape?: 'tag' | 'pill'
  size?: 'sm' | 'md'
  /** Optional icon element; rendered aria-hidden (white in the category disc, deep palette colour on a label). */
  icon?: ReactNode
  /**
   * A control after the name, such as a remove button. It sits outside the truncating
   * name: a long name is cut off with an ellipsis instead of pushing the control out of
   * sight (the name's overflow is hidden, so anything inside it would be clipped).
   */
  trailing?: ReactNode
}

/**
 * The chip for categories and labels (spec-63, ED-13, DS-9; look spec-64-ul). The name is
 * always visible text; colour, icon, disc and flame are decoration (IA-20). The look lives
 * in `.vonk-chip` (index.css); this component only hands over the palette as custom
 * properties.
 */
export function Chip({
  color,
  shape = 'tag',
  size = 'sm',
  icon,
  trailing,
  className,
  style,
  children,
  ...props
}: ChipProps) {
  const pill = shape === 'pill'
  // React ids contain ':' or '«»'; an SVG url(#id) reference needs a plain one.
  const gradientId = `vonk-flame-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const palette = {
    '--chip-light': tagVar(color, 'light'),
    '--chip-deep': tagVar(color, 'deep'),
    '--chip-glow': tagVar(color, 'glow'),
  } as CSSProperties
  return (
    <span
      data-slot="chip"
      data-shape={shape}
      data-size={size}
      data-color={color}
      className={cn('vonk-chip', className)}
      style={{ ...palette, ...style }}
      {...props}
    >
      {pill ? (
        icon ? (
          <span aria-hidden data-slot="chip-icon" className="vonk-chip__icon vonk-duotone">
            {icon}
          </span>
        ) : (
          <FlameIcon aria-hidden data-slot="chip-flame" className="vonk-chip__flame" gradientId={gradientId}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" style={{ stopColor: 'var(--chip-flame-top)' }} />
                <stop offset="1" style={{ stopColor: 'var(--chip-deep)' }} />
              </linearGradient>
            </defs>
          </FlameIcon>
        )
      ) : (
        <span aria-hidden data-slot="chip-disc" className="vonk-chip__disc vonk-duotone">
          {icon}
        </span>
      )}
      {/* The full name as a tooltip: a long one is cut off. */}
      <span className="truncate" title={typeof children === 'string' ? children : undefined}>
        {children}
      </span>
      {trailing ? (
        <span data-slot="chip-trailing" className="-mr-1 flex shrink-0 items-center">
          {trailing}
        </span>
      ) : null}
    </span>
  )
}
